import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ideasAPI, categoriesAPI } from '../lib/api';
import { getCategoryStyle, getPriorityStyle, getStatusStyle, formatRelativeTime, truncateText } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Plus, 
  Lightbulb, 
  Zap,
  Search,
  Filter,
  MoreVertical,
  Link as LinkIcon,
  Trash2,
  Edit,
  Eye,
  Video,
  Sparkles,
  Palette,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Kanban columns - Backlog, Shortlisted, In Production, Completed
const KANBAN_COLUMNS = ['Backlog', 'Shortlisted', 'In Production', 'Completed'];

// Idea type configurations
const IDEA_TYPE_CONFIG = {
  'content': { label: 'Content Ideas', icon: Video, description: 'Video content and reels' },
  'stories': { label: 'Story Ideas', icon: Sparkles, description: 'Stories and ephemeral content' },
  'custom-ip': { label: 'Custom IP Ideas', icon: Palette, description: 'Custom intellectual property designs' },
};

const Ideas = ({ ideaType = 'content' }) => {
  const { isAdmin, isFounder, activeBrand, isIdeaManager } = useAuth();
  const isFounderUser = isFounder();
  const typeConfig = IDEA_TYPE_CONFIG[ideaType] || IDEA_TYPE_CONFIG['content'];
  const TypeIcon = typeConfig.icon;
  const [ideas, setIdeas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [editingIdea, setEditingIdea] = useState(null);
  const [deleteConfirmIdea, setDeleteConfirmIdea] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [greenlightDialog, setGreenlightDialog] = useState(null);
  
  const [newIdea, setNewIdea] = useState({
    title: '',
    category: '',
    source: 'Agency',
    reference_reel_link_1: '',
    reference_reel_link_2: '',
    reference_reel_link_3: '',
    priority: 'P1',
    description: '',
    idea_type: ideaType, // Add idea type
  });

  const [editFormData, setEditFormData] = useState({
    title: '',
    category: '',
    source: '',
    reference_reel_link_1: '',
    reference_reel_link_2: '',
    reference_reel_link_3: '',
    priority: '',
    description: '',
  });

  useEffect(() => {
    if (activeBrand) {
      fetchData();
    }
  }, [activeBrand, ideaType]); // Re-fetch when ideaType changes

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ideasRes, categoriesRes] = await Promise.all([
        ideasAPI.getAll({ idea_type: ideaType }, activeBrand),
        categoriesAPI.getAll(),
      ]);
      setIdeas(ideasRes.data);
      setCategories(categoriesRes.data.categories);
      setPriorities(categoriesRes.data.idea_priorities);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIdea = async () => {
    if (!newIdea.title || !newIdea.category) {
      toast.error('Title and category are required');
      return;
    }
    
    try {
      await ideasAPI.create({ ...newIdea, idea_type: ideaType }, activeBrand);
      toast.success('Idea created!');
      setShowAddSheet(false);
      setNewIdea({
        title: '',
        category: '',
        source: 'Agency',
        reference_reel_link_1: '',
        reference_reel_link_2: '',
        reference_reel_link_3: '',
        priority: 'P1',
        description: '',
        idea_type: ideaType,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create idea');
    }
  };

  const handleUpdateIdea = async (id, updates) => {
    try {
      await ideasAPI.update(id, updates);
      toast.success('Updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleEditIdea = (idea) => {
    setEditFormData({
      title: idea.title || '',
      category: idea.category || '',
      source: idea.source || '',
      reference_reel_link_1: idea.reference_reel_link_1 || '',
      reference_reel_link_2: idea.reference_reel_link_2 || '',
      reference_reel_link_3: idea.reference_reel_link_3 || '',
      priority: idea.priority || 'P1',
      description: idea.description || '',
    });
    setEditingIdea(idea);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.title || !editFormData.category) {
      toast.error('Title and category are required');
      return;
    }
    
    try {
      await ideasAPI.update(editingIdea.id, editFormData);
      toast.success('Idea updated!');
      setEditingIdea(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update idea');
    }
  };

  const handleGreenlight = async (idea) => {
    try {
      await ideasAPI.greenlight(idea.id);
      toast.success('Idea greenlit! Production pack created.');
      setGreenlightDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to greenlight');
    }
  };

  const handleShortlistIdea = async (idea) => {
    try {
      await ideasAPI.update(idea.id, { status: 'Shortlisted' });
      toast.success('Idea moved to Shortlisted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to shortlist idea');
    }
  };

  const handleDeleteIdea = async (id) => {
    try {
      await ideasAPI.delete(id);
      toast.success('Idea deleted!');
      setSelectedIdea(null);
      setDeleteConfirmIdea(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getIdeasByStatus = (status) => {
    return ideas.filter((idea) => {
      // Map statuses to columns
      let matchesStatus = false;
      if (status === 'Backlog') {
        matchesStatus = idea.status === 'Backlog';
      } else if (status === 'Shortlisted') {
        matchesStatus = idea.status === 'Shortlisted';
      } else if (status === 'In Production') {
        matchesStatus = idea.status === 'In Production' || idea.status === 'Greenlit';
      } else if (status === 'Completed') {
        matchesStatus = idea.status === 'Completed' || idea.status === 'Posted';
      } else {
        matchesStatus = idea.status === status;
      }
      
      const matchesCategory = filterCategory === 'all' || idea.category === filterCategory;
      const matchesPriority = filterPriority === 'all' || idea.priority === filterPriority;
      const matchesSearch = idea.title.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesCategory && matchesPriority && matchesSearch;
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="ideas-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-4">
              <div className="h-6 bg-zinc-100 rounded w-24" />
              {[1, 2].map((j) => (
                <div key={j} className="h-32 bg-zinc-100 rounded" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="ideas-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <TypeIcon className="w-8 h-8 text-zinc-700" />
            <h1 
              className="text-3xl font-medium text-zinc-900"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {typeConfig.label}
            </h1>
          </div>
          <p className="text-zinc-500 mt-1">{typeConfig.description}</p>
        </div>
        
        <Button
          onClick={() => setShowAddSheet(true)}
          className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
          data-testid="add-idea-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Idea
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 bg-white border border-zinc-100 p-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search ideas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-400"
            data-testid="ideas-search-input"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full md:w-48" data-testid="ideas-category-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-full md:w-36" data-testid="ideas-priority-filter">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {priorities.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {KANBAN_COLUMNS.map((column) => {
          const columnIdeas = getIdeasByStatus(column);
          const isInProduction = column === 'In Production';
          const isCompleted = column === 'Completed';
          
          return (
            <div 
              key={column} 
              className="bg-zinc-50/50 rounded-sm p-4"
              data-testid={`kanban-column-${column.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-zinc-700 uppercase tracking-widest">
                  {column}
                </h3>
                <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">
                  {columnIdeas.length}
                </span>
              </div>
              
              <div className="space-y-3">
                {columnIdeas.map((idea) => {
                  const categoryStyle = getCategoryStyle(idea.category);
                  const priorityStyle = getPriorityStyle(idea.priority);
                  
                  return (
                    <div
                      key={idea.id}
                      className="bg-white border border-zinc-100 p-4 cursor-pointer transition-all duration-300 hover:border-zinc-300 hover:shadow-lg group"
                      data-testid={`idea-card-${idea.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <h4 className="text-sm font-medium text-zinc-900 flex-1 pr-2">
                          {truncateText(idea.title, 50)}
                        </h4>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setSelectedIdea(idea)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditIdea(idea)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            {/* Shortlist option for Backlog items */}
                            {idea.status === 'Backlog' && isIdeaManager() && (
                              <DropdownMenuItem 
                                onClick={() => handleShortlistIdea(idea)}
                                className="text-blue-600"
                              >
                                <ArrowRight className="w-4 h-4 mr-2" /> Move to Shortlist
                              </DropdownMenuItem>
                            )}
                            {/* Greenlight option for Shortlisted items */}
                            {idea.status === 'Shortlisted' && isIdeaManager() && (
                              <DropdownMenuItem 
                                onClick={() => setGreenlightDialog(idea)}
                                className="text-[#D4AF37]"
                              >
                                <Zap className="w-4 h-4 mr-2" /> Greenlight (Move to Production)
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteConfirmIdea(idea)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      {idea.description && (
                        <p className="text-xs text-zinc-500 mt-2">{truncateText(idea.description, 60)}</p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${categoryStyle.bg} ${categoryStyle.text}`}>
                          {idea.category.split(' ')[0]}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${priorityStyle.bg} ${priorityStyle.text}`}>
                          {idea.priority}
                        </span>
                        {isFounderUser && idea.creator_name && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-100"
                            title={idea.creator_email || idea.creator_name}
                            data-testid={`idea-creator-badge-${idea.id}`}
                          >
                            by {idea.creator_name}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100">
                        <span className="text-xs text-zinc-400">{idea.source}</span>
                        <span className="text-xs text-zinc-400">{formatRelativeTime(idea.created_at)}</span>
                      </div>
                      
                      {/* Shortlist button for Backlog items */}
                      {idea.status === 'Backlog' && isIdeaManager() && (
                        <Button
                          onClick={() => handleShortlistIdea(idea)}
                          className="w-full mt-3 bg-blue-500 hover:bg-blue-600 text-white rounded-sm py-2 text-xs uppercase tracking-widest"
                          data-testid={`shortlist-btn-${idea.id}`}
                        >
                          <ArrowRight className="w-3 h-3 mr-2" />
                          Shortlist
                        </Button>
                      )}
                      
                      {/* Greenlight button for Shortlisted items */}
                      {idea.status === 'Shortlisted' && isIdeaManager() && (
                        <Button
                          onClick={() => setGreenlightDialog(idea)}
                          className="w-full mt-3 bg-[#D4AF37] hover:bg-amber-600 text-white rounded-sm py-2 text-xs uppercase tracking-widest"
                          data-testid={`greenlight-btn-${idea.id}`}
                        >
                          <Zap className="w-3 h-3 mr-2" />
                          Greenlight
                        </Button>
                      )}
                    </div>
                  );
                })}
                
                {columnIdeas.length === 0 && (
                  <div className="text-center py-8 text-zinc-400 text-sm">
                    No ideas here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Idea Sheet */}
      <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="add-idea-sheet">
          <SheetHeader className="pb-6 border-b border-zinc-100">
            <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>New Idea</SheetTitle>
            <p className="text-sm text-zinc-500 mt-1">Add a new content idea to your pipeline</p>
          </SheetHeader>
          
          <div className="space-y-5 mt-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newIdea.title}
                onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="What's the idea?"
                data-testid="add-idea-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <Select
                value={newIdea.category}
                onValueChange={(value) => setNewIdea({ ...newIdea, category: value })}
              >
                <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-idea-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Source
                </label>
                <Select
                  value={newIdea.source}
                  onValueChange={(value) => setNewIdea({ ...newIdea, source: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-idea-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Founder">Founder</SelectItem>
                    <SelectItem value="Agency">Agency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Priority
                </label>
                <Select
                  value={newIdea.priority}
                  onValueChange={(value) => setNewIdea({ ...newIdea, priority: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-idea-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P0">P0 - Critical</SelectItem>
                    <SelectItem value="P1">P1 - High</SelectItem>
                    <SelectItem value="P2">P2 - Normal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Reference #1
              </label>
              <input
                type="url"
                value={newIdea.reference_reel_link_1}
                onChange={(e) => setNewIdea({ ...newIdea, reference_reel_link_1: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="Paste reel link here..."
                data-testid="add-idea-reference-link-1-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Reference #2
              </label>
              <input
                type="url"
                value={newIdea.reference_reel_link_2}
                onChange={(e) => setNewIdea({ ...newIdea, reference_reel_link_2: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="Paste reel link here..."
                data-testid="add-idea-reference-link-2-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Reference #3
              </label>
              <input
                type="url"
                value={newIdea.reference_reel_link_3}
                onChange={(e) => setNewIdea({ ...newIdea, reference_reel_link_3: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                placeholder="Paste reel link here..."
                data-testid="add-idea-reference-link-3-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Description
              </label>
              <textarea
                value={newIdea.description}
                onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all min-h-[120px] resize-none"
                placeholder="Add more details about this idea..."
                data-testid="add-idea-description-input"
              />
            </div>

            <Button
              onClick={handleCreateIdea}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-md py-4 text-sm font-medium tracking-wide transition-all mt-4"
              data-testid="save-idea-btn"
            >
              Create Idea
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Idea Detail Sheet */}
      <Sheet open={!!selectedIdea} onOpenChange={() => setSelectedIdea(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="idea-detail-sheet">
          {selectedIdea && (
            <>
              <SheetHeader className="pb-6 border-b border-zinc-100">
                <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>Idea Details</SheetTitle>
              </SheetHeader>
              
              <div className="space-y-5 mt-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Title</label>
                  <h3 className="text-xl font-medium text-zinc-900">{selectedIdea.title}</h3>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className={`text-sm px-3 py-1 rounded-md ${getCategoryStyle(selectedIdea.category).bg} ${getCategoryStyle(selectedIdea.category).text}`}>
                    {selectedIdea.category}
                  </span>
                  <span className={`text-sm px-3 py-1 rounded-md ${getPriorityStyle(selectedIdea.priority).bg} ${getPriorityStyle(selectedIdea.priority).text}`}>
                    {selectedIdea.priority}
                  </span>
                  <span className={`text-sm px-3 py-1 rounded-md ${getStatusStyle(selectedIdea.status).bg} ${getStatusStyle(selectedIdea.status).text}`}>
                    {selectedIdea.status}
                  </span>
                </div>

                {selectedIdea.description && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Description</label>
                    <p className="text-zinc-900 leading-relaxed">{selectedIdea.description}</p>
                  </div>
                )}

                {(selectedIdea.reference_reel_link_1 || selectedIdea.reference_reel_link_2 || selectedIdea.reference_reel_link_3) && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Reference Reels</label>
                    <div className="space-y-2">
                      {selectedIdea.reference_reel_link_1 && (
                        <a 
                          href={selectedIdea.reference_reel_link_1}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 break-all"
                        >
                          <LinkIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="font-medium mr-1">#1:</span> {selectedIdea.reference_reel_link_1}
                        </a>
                      )}
                      {selectedIdea.reference_reel_link_2 && (
                        <a 
                          href={selectedIdea.reference_reel_link_2}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 break-all"
                        >
                          <LinkIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="font-medium mr-1">#2:</span> {selectedIdea.reference_reel_link_2}
                        </a>
                      )}
                      {selectedIdea.reference_reel_link_3 && (
                        <a 
                          href={selectedIdea.reference_reel_link_3}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 break-all"
                        >
                          <LinkIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="font-medium mr-1">#3:</span> {selectedIdea.reference_reel_link_3}
                        </a>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Update Status</label>
                  <Select
                    value={selectedIdea.status}
                    onValueChange={(value) => {
                      handleUpdateIdea(selectedIdea.id, { status: value });
                      setSelectedIdea({ ...selectedIdea, status: value });
                    }}
                  >
                    <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="idea-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Backlog">Backlog</SelectItem>
                      <SelectItem value="Shortlisted">Shortlisted</SelectItem>
                      <SelectItem value="Archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Source</label>
                    <p className="text-zinc-900">{selectedIdea.source}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Created</label>
                    <p className="text-zinc-900">{formatRelativeTime(selectedIdea.created_at)}</p>
                  </div>
                </div>

                {selectedIdea.production_pack_id && (
                  <div className="p-4 bg-emerald-50 rounded-md">
                    <p className="text-sm font-medium text-emerald-700">Production Pack Created</p>
                    <a 
                      href={`/production-packs/${selectedIdea.production_pack_id}`}
                      className="text-emerald-600 hover:text-emerald-700 text-sm mt-1 inline-flex items-center gap-1"
                    >
                      <LinkIcon className="w-3 h-3" />
                      View Production Pack
                    </a>
                  </div>
                )}

                <div className="pt-4 border-t border-zinc-100 space-y-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleEditIdea(selectedIdea);
                      setSelectedIdea(null);
                    }}
                    className="w-full rounded-md py-3"
                    data-testid="edit-idea-from-detail-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Idea
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setDeleteConfirmIdea(selectedIdea);
                      setSelectedIdea(null);
                    }}
                    className="w-full rounded-md py-3"
                    data-testid="delete-idea-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Idea
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Greenlight Confirmation Dialog */}
      <Dialog open={!!greenlightDialog} onOpenChange={() => setGreenlightDialog(null)}>
        <DialogContent data-testid="greenlight-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              <Zap className="w-5 h-5 inline-block mr-2 text-[#D4AF37]" />
              Greenlight This Idea?
            </DialogTitle>
            <DialogDescription>
              This will create a Production Pack and move the idea to production. Only greenlight ideas that are ready for execution.
            </DialogDescription>
          </DialogHeader>
          
          {greenlightDialog && (
            <div className="py-4">
              <h4 className="font-medium text-zinc-900">{greenlightDialog.title}</h4>
              <p className="text-sm text-zinc-500 mt-1">{greenlightDialog.category}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGreenlightDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleGreenlight(greenlightDialog)}
              className="bg-[#D4AF37] hover:bg-amber-600 text-white"
              data-testid="confirm-greenlight-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Greenlight
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmIdea} onOpenChange={() => setDeleteConfirmIdea(null)}>
        <DialogContent data-testid="delete-idea-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              <Trash2 className="w-5 h-5 inline-block mr-2 text-red-500" />
              Delete This Idea?
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The idea will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          {deleteConfirmIdea && (
            <div className="py-4">
              <h4 className="font-medium text-zinc-900">{deleteConfirmIdea.title}</h4>
              <p className="text-sm text-zinc-500 mt-1">{deleteConfirmIdea.category}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmIdea(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDeleteIdea(deleteConfirmIdea.id)}
              data-testid="confirm-delete-idea-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Idea Sheet */}
      <Sheet open={!!editingIdea} onOpenChange={() => setEditingIdea(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="edit-idea-sheet">
          {editingIdea && (
            <>
              <SheetHeader className="pb-6 border-b border-zinc-100">
                <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Edit className="w-5 h-5 inline-block mr-2" />
                  Edit Idea
                </SheetTitle>
                <p className="text-sm text-zinc-500 mt-1">Update the idea details</p>
              </SheetHeader>
              
              <div className="space-y-5 mt-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.title}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                    placeholder="What's the idea?"
                    data-testid="edit-idea-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={editFormData.category}
                    onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}
                  >
                    <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md" data-testid="edit-idea-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Priority</label>
                    <Select
                      value={editFormData.priority}
                      onValueChange={(value) => setEditFormData({ ...editFormData, priority: value })}
                    >
                      <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md" data-testid="edit-idea-priority-select">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {priorities.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Source</label>
                    <Select
                      value={editFormData.source}
                      onValueChange={(value) => setEditFormData({ ...editFormData, source: value })}
                    >
                      <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md" data-testid="edit-idea-source-select">
                        <SelectValue placeholder="Source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Agency">Agency</SelectItem>
                        <SelectItem value="Founder">Founder</SelectItem>
                        <SelectItem value="Inspo">Inspo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Description</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all resize-none"
                    placeholder="Add more details about this idea..."
                    data-testid="edit-idea-description-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    <LinkIcon className="w-4 h-4 inline-block mr-1" />
                    Reference Reels
                  </label>
                  <div className="space-y-3">
                    <input
                      type="url"
                      value={editFormData.reference_reel_link_1}
                      onChange={(e) => setEditFormData({ ...editFormData, reference_reel_link_1: e.target.value })}
                      className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                      placeholder="Reference reel link #1"
                      data-testid="edit-idea-ref1-input"
                    />
                    <input
                      type="url"
                      value={editFormData.reference_reel_link_2}
                      onChange={(e) => setEditFormData({ ...editFormData, reference_reel_link_2: e.target.value })}
                      className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                      placeholder="Reference reel link #2"
                      data-testid="edit-idea-ref2-input"
                    />
                    <input
                      type="url"
                      value={editFormData.reference_reel_link_3}
                      onChange={(e) => setEditFormData({ ...editFormData, reference_reel_link_3: e.target.value })}
                      className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all"
                      placeholder="Reference reel link #3"
                      data-testid="edit-idea-ref3-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveEdit}
                  className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-md py-4 text-sm font-medium tracking-wide transition-all mt-4"
                  data-testid="save-edit-idea-btn"
                >
                  Save Changes
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Ideas;
